import { doLogout } from '@/state/reducers/authSlice';

import {
    isGlobalBlockPlatform,
    isPaytechPlatform,
    isWolfBTCPlatform,
    shouldUseNopBalance
} from '@/helpers/environmentHelper';
import { logToServer } from '@/services/LogService';
import { changeBalanceAccount } from '@/state/reducers/balanceSlice';
import history from '@/utils/history';
import { useDisclosure } from '@/utils/hooks/useDisclosure';
import useScreenSizes from '@/utils/hooks/useScreenSizes';
import cn from 'classnames';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import EnvironmentBadge from '../common/EnvironmentBadge';
import SettingsModal from '../modal/SettingsModal';
import DesktopNavbar from './DesktopNavbar';
import LiteMobileNavbar from './lite/LiteMobileNavbar';
import MobileDrawer from './MobileDrawer';
import MobileNavbar from './MobileNavbar';
import PortalMobileDrawer from './portal/PortalMobileDrawer';
import PortalMobileNavbar from './portal/PortalMobileNavbar';

const xplorspot_logo = window.config.logo;
const lite_logo = window.config.lite_logo;
const portal_logo = window.config.portal_logo;
const xplortrader_logo = window.config.trader_logo;

export const onLogout = async (credentials, dispatch) => {
    try {
        if (credentials) {
            logToServer('info', `User ${credentials?.username} has manually logged out using navbar.`);
            await dispatch(doLogout({ credentials }) as any).unwrap();
        }
    } catch (rejectedValueOrSerializedError) {
        //
    }
    history.push('/login');
};

export const onUserChange = async (credentials, dispatch) => {
    if (credentials && !shouldUseNopBalance) await dispatch(changeBalanceAccount({ credentials }));
};

export default function Navbar({ settings }: { settings?: ReturnType<typeof useDisclosure> }) {
    const location = useLocation();

    const drawer = useDisclosure(false);
    const { mobile, tablet } = useScreenSizes();

    const isMobile = useMemo(() => mobile || tablet, [mobile, tablet]);

    const { isXplorSpot, isXplorSpotLite, isXplorSpotPortal, isXplorTrader } = useMemo(() => {
        return {
            isXplorSpot: location.pathname === '/' || location.pathname === '',
            isXplorSpotLite: location.pathname.includes('/lite'),
            isXplorSpotPortal: location.pathname.includes('/portal'),
            isXplorTrader: location.pathname.includes('/trader')
        };
    }, [location]);

    const imageSrc = useMemo(() => {
        if (isXplorSpot) {
            return xplorspot_logo;
        } else if (isXplorSpotLite) {
            return lite_logo;
        } else if (isXplorSpotPortal) {
            return portal_logo;
        } else if (isXplorTrader) {
            return xplortrader_logo;
        } else {
            return '';
        }
    }, [isXplorSpot, isXplorSpotLite, isXplorSpotPortal, isXplorTrader]);

    return (
        <header
            className={cn('flex justify-center w-full bg-brand-background z-50', {
                'absolute lg:fixed lg:top-0 lg:left-0 lg:pl-[260px]': isXplorSpotPortal
            })}>
            <div
                className={cn(
                    'flex w-full justify-between relative lg:items-center gap-3 lg:px-6 p-1 border-brand-primary border-b-[1.5px] lg:border-b-[3px] lg:h-[67px]',
                    { 'px-4': !isMobile }
                )}>
                <div className="flex flex-row items-center gap-3">
                    <EnvironmentBadge />
                    {isMobile && (
                        <div className="flex flex-row h-full items-center gap-3">
                            {!(isPaytechPlatform || isWolfBTCPlatform || isGlobalBlockPlatform) && (
                                <img className="block h-auto max-h-10 xsm:max-h-12 xsm:h-12" src={imageSrc} />
                            )}
                            {(isPaytechPlatform || isWolfBTCPlatform || isGlobalBlockPlatform) && (
                                <img className="block h-auto max-h-8 xs:max-h-10 xs:h-10 pl-1" src={imageSrc} />
                            )}
                            <EnvironmentBadge />
                        </div>
                    )}
                    {isPaytechPlatform && (
                        <div className="text-xs text-brand-module-title hidden xsm:block">Powered by GCEX</div>
                    )}
                </div>
                {(isXplorSpot || isXplorTrader) && <MobileNavbar drawerDisclosure={drawer} />}
                {isXplorSpotLite && <LiteMobileNavbar drawerDisclosure={drawer} />}
                {isXplorSpotPortal && <PortalMobileNavbar drawerDisclosure={drawer} />}
                <DesktopNavbar isPortal={isXplorSpotPortal} />
            </div>
            {(isXplorSpot || isXplorTrader || isXplorSpotLite) && (
                <MobileDrawer settingsDisclosure={settings} drawerDisclosure={drawer} />
            )}
            {isXplorSpotPortal && <PortalMobileDrawer drawerDisclosure={drawer} />}
            {!isXplorSpotPortal && settings && <SettingsModal opened={settings[0]} handlers={settings[1]} />}
        </header>
    );
}
